import {
  keepPreviousData,
  partialMatchKey,
  queryOptions,
  skipToken,
  type QueryClient,
  type UseQueryOptions,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  BroadcastTrigger,
  CampaignsCampaignDraftResponse,
  Metric,
  CampaignsNextProfilesResponse,
  CampaignsProfileMatchTimeRequest,
  CampaignsProfileMatchTimeResponse,
  CampaignsValidateResponse,
  PreviewsObjectDatePreviewRequest,
  PreviewsObjectDatePreviewResponse,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import {
  getApiClientErrorBody,
  getApiClientErrorCode,
  getApiClientErrorDetail,
  getApiClientErrorStatus,
} from '@/services/api/errors';
import type { Action } from '../action/entity';
import { broadcastTriggerKeys } from '../broadcast-trigger/entity';
import { getActionQueryOptions } from '../action/queries';
import type { QueryConfig } from '../shared/react-query-types';
import { useAllCached, type UseAllCachedOptions } from '../shared/use-all-cached';
import { useEntitiesByIds } from '../shared/use-entities-by-ids';
import { toWireIds } from '../shared/wire-ids';
import { requireWorkspaceId } from '../shared/workspace';
import type { Tag } from '../tag/entity';
import { getTagQueryOptions } from '../tag/queries';
import {
  parseCampaignModel,
  parseCampaignsListResponse,
  populateCampaignModel,
  populateCampaignsList,
  toCampaignWorkflowDetail,
  type CampaignWorkflowDetail,
  type ParsedCampaignModel,
  type ParsedCampaignsListResponse,
} from './populate';
import {
  campaignKeys,
  type ApiTriggeredBroadcastListParams,
  type Campaign,
  type CampaignListMetricsWindow,
  type CampaignListParams,
} from './entity';

/**
 * Campaign read queries.
 *
 * Each query is exposed as:
 *
 *   1. A plain async fetcher (`getCampaigns(...)`, `getCampaign(...)`) —
 *      callable from anywhere, used by routers / loaders / non-React code.
 *   2. A `getXQueryOptions` factory returning a `queryOptions(...)` config
 *      — composable with `useQuery`, `useSuspenseQuery`, `prefetchQuery`,
 *      `useQueries`, etc.
 *   3. A thin hook wrapper (`useCampaigns`, `useCampaign`) that calls
 *      `useSuspenseQuery` with the options + caller-side `queryConfig`.
 *
 * Callers compose at whichever layer fits the use case.
 */

const STALE_TIME = 60_000;

/* ------------------------------------------------------------------------- */
/* getCampaigns — paginated list with cluster sideload                        */
/* ------------------------------------------------------------------------- */

function listMetricsQuery(window: CampaignListMetricsWindow | undefined, rowsOnly: boolean) {
  if (rowsOnly) return { ...window, excludeActions: true, excludeMetrics: true, excludeTags: true };
  return window ?? { excludeMetrics: true };
}

/** @public */
export async function getCampaigns(
  workspaceId: number,
  params: CampaignListParams,
  qc: QueryClient,
): Promise<ParsedCampaignsListResponse> {
  const raw = await hydraUiApi.campaigns.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: {
        page: params.page - 1, // 1-based at hook boundary, 0-based for backend
        state: params.state,
        type: params.type,
        search: params.search,
        'searchType[]': params.searchTypes,
        subscriptionTopicId: params.subscriptionTopicId,
        sendToUnsubscribed: params.sendToUnsubscribed,
        'tagIds[]': toWireIds(params.tagIds),
        'objectTypeIds[]': toWireIds(params.objectTypeIds),
        'goalIds[]': toWireIds(params.goalIds),
        sort: params.sort,
        asc: params.asc,
        archived: params.archived,
        version: '2',
        ...listMetricsQuery(params.metrics, params.rowsOnly === true),
      },
    },
  });
  const response = parseCampaignsListResponse(raw);
  if (params.rowsOnly === true) return response;
  // Read-side cluster propagation — fans detail caches for every entity in
  // the cluster (under the workspace's scope prefix), populates per-campaign
  // relation slots. Runs synchronously inside the queryFn so by-id and
  // relation hooks see the data on the same render pass.
  populateCampaignsList(qc, workspaceId, response);
  return response;
}

/**
 * How many automations the list counts for the workspace, the number an untitled one is named
 * after. Reads one page without its actions, metrics or tags, and fills no cache: a count taken at
 * create time is stale the moment the automation exists.
 *
 * @public
 */
export async function getCampaignsTotal(workspaceId: number): Promise<number> {
  const raw = await hydraUiApi.campaigns.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { page: 0, version: '2', excludeActions: true, excludeMetrics: true, excludeTags: true },
    },
  });
  return raw.meta?.pagination?.total ?? 0;
}

/**
 * Whether the workspace still has campaigns on the legacy segment trigger,
 * which the list reports in its totals. Reads one page without its actions,
 * metrics or tags, and fills no cache with its rows.
 */
export async function getHasLegacySegmentTriggeredCampaigns(workspaceId: number): Promise<boolean> {
  const raw = await hydraUiApi.campaigns.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { page: 0, version: '2', excludeActions: true, excludeMetrics: true, excludeTags: true },
    },
  });
  return raw.meta?.totals?.has_legacy_segment_triggered_campaigns ?? false;
}

/** @public */
export const useHasLegacySegmentTriggeredCampaigns = (workspaceId: number | null) =>
  useQuery({
    queryKey:
      workspaceId == null
        ? ['campaign', 'has-legacy-segment-triggered', 'inactive']
        : campaignKeys.hasLegacySegmentTriggered(workspaceId),
    queryFn: workspaceId == null ? skipToken : () => getHasLegacySegmentTriggeredCampaigns(workspaceId),
    staleTime: STALE_TIME,
  });

export const getCampaignsQueryOptions = (workspaceId: number, params: CampaignListParams, qc: QueryClient) =>
  queryOptions({
    queryKey: campaignKeys.list(workspaceId, params),
    queryFn: () => getCampaigns(workspaceId, params, qc),
    staleTime: STALE_TIME,
  });

interface UseCampaignsOptions {
  params: CampaignListParams;
  queryConfig?: QueryConfig<typeof getCampaignsQueryOptions>;
}

/**
 * Throws synchronously during render if no workspace id is in the URL —
 * see `requireWorkspaceId`. For layouts/banners that may mount before
 * the URL settles, use `useCampaignsOptional` below.
 */
/** @public */
export const useCampaigns = ({ params, queryConfig }: UseCampaignsOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getCampaignsQueryOptions(workspaceId, params, qc),
    ...queryConfig,
  });
};

interface UseCampaignsOptionalOptions {
  workspaceId: number | null;
  params: CampaignListParams;
  queryConfig?: QueryConfig<typeof getCampaignsQueryOptions>;
  // Typed against the raw list response: the refetch-interval callback
  // inspects `query.state.data`, which is always the fetched data.
  refetchInterval?: UseQueryOptions<
    ParsedCampaignsListResponse,
    Error,
    ParsedCampaignsListResponse,
    ReturnType<typeof campaignKeys.list>
  >['refetchInterval'];
  refetchIntervalInBackground?: boolean;
}

/**
 * Non-suspending list read for callers that may mount before the URL's
 * workspace id settles. `skipToken` keeps the no-workspace render from
 * fetching (and from capturing a stale QueryClient in the queryFn closure);
 * the `?? 0` key slot mirrors `usePipelinesConnectionsOptional` — nothing
 * ever lands there.
 *
 * @public
 */
export const useCampaignsOptional = ({
  workspaceId,
  params,
  queryConfig,
  refetchInterval,
  refetchIntervalInBackground,
}: UseCampaignsOptionalOptions) => {
  const qc = useQueryClient();
  const validWorkspaceId = workspaceId != null && workspaceId > 0 ? workspaceId : null;
  return useQuery({
    queryKey: campaignKeys.list(validWorkspaceId ?? 0, params),
    queryFn: validWorkspaceId != null ? () => getCampaigns(validWorkspaceId, params, qc) : skipToken,
    staleTime: STALE_TIME,
    refetchInterval,
    refetchIntervalInBackground,
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getCampaign — by-id with cluster sideload                                  */
/* ------------------------------------------------------------------------- */

/**
 * The one `GET /campaigns/:id` request: fetch, parse, and propagate the
 * CampaignModel envelope. `getCampaign` and `getCampaignWorkflowDetail` each
 * return their own view of the result.
 */
export async function fetchCampaignModel(
  workspaceId: number,
  campaignId: string,
  qc: QueryClient,
): Promise<ParsedCampaignModel> {
  const raw = await hydraUiApi.campaigns.get({
    params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
  });
  const parsed = parseCampaignModel(raw);
  populateCampaignModel(qc, workspaceId, parsed);
  // Only this read carries the latest trigger, and its absence here means the
  // broadcast has none yet.
  qc.setQueryData(broadcastTriggerKeys.mostRecent(workspaceId, campaignId), parsed.most_recent_trigger ?? null);
  return parsed;
}

/** @public */
export async function getCampaign(workspaceId: number, campaignId: string, qc: QueryClient): Promise<Campaign> {
  const parsed = await fetchCampaignModel(workspaceId, campaignId, qc);
  return parsed.campaign;
}

/** @public */
export const getCampaignQueryOptions = (workspaceId: number, campaignId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: campaignKeys.detail(workspaceId, campaignId),
    queryFn: () => getCampaign(workspaceId, campaignId, qc),
    staleTime: STALE_TIME,
  });

interface UseCampaignOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getCampaignQueryOptions>;
}

/** @public */
export const useCampaign = ({ id, queryConfig }: UseCampaignOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getCampaignQueryOptions(workspaceId, id, qc),
    ...queryConfig,
  });
};

interface UseCampaignOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled by-id hook. For callers resolving a
 * campaign outside a suspense boundary that may keep the query dormant.
 * `skipToken` (not `enabled: false`) so no cache slot is registered while
 * inactive; `retry: false` so a 404 surfaces immediately for deleted-resource
 * detection.
 *
 * @public
 */
export const useCampaignOptional = ({ workspaceId, id, enabled = true }: UseCampaignOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? campaignKeys.detail(workspaceId, id) : ['campaign', 'inactive'],
    queryFn: active ? () => getCampaign(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};

/* ------------------------------------------------------------------------- */
/* getCampaignWorkflowDetail — campaign + detail-shaped actions, one slot     */
/* ------------------------------------------------------------------------- */

/**
 * A campaign and its actions exactly as `GET /campaigns/:id` returns them.
 *
 * Action detail slots are shared with the campaigns-list sideload, whose
 * rows are the sparse `ActionForCampaignsList` shape. Reading actions through
 * `useCampaignActions` therefore cannot promise the detail fields the
 * workflow canvas sizes on. This read keeps its own slot
 * (`campaignKeys.workflowDetail`), which only a CampaignModel envelope ever
 * writes — this fetch, and every mutation that goes through
 * `populateCampaignModel`.
 *
 * @public
 */
export async function getCampaignWorkflowDetail(
  workspaceId: number,
  campaignId: string,
  qc: QueryClient,
): Promise<CampaignWorkflowDetail> {
  const parsed = await fetchCampaignModel(workspaceId, campaignId, qc);
  return toCampaignWorkflowDetail(parsed);
}

/** @public */
export const getCampaignWorkflowDetailQueryOptions = (workspaceId: number, campaignId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: campaignKeys.workflowDetail(workspaceId, campaignId),
    queryFn: () => getCampaignWorkflowDetail(workspaceId, campaignId, qc),
    staleTime: STALE_TIME,
  });

interface UseCampaignWorkflowDetailOptions {
  id: string;
  /** The campaign's workspace; the URL's when left out. */
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getCampaignWorkflowDetailQueryOptions>;
}

/** @public */
export const useCampaignWorkflowDetail = ({ id, workspaceId, queryConfig }: UseCampaignWorkflowDetailOptions) => {
  const resolvedWorkspaceId = workspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getCampaignWorkflowDetailQueryOptions(resolvedWorkspaceId, id, qc),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getNextProfiles — the people a date campaign enters next                   */
/* ------------------------------------------------------------------------- */

/**
 * The people a running date campaign is scheduled to enter next, soonest
 * first, and how many are scheduled in all (`meta.pagination.total`).
 *
 * Only the first page is asked for. The endpoint also takes a `from` due time
 * and a `continuation` token to read further, which nothing reads yet.
 *
 * @public
 */
export async function getNextProfiles(workspaceId: number, campaignId: string): Promise<CampaignsNextProfilesResponse> {
  return hydraUiApi.campaigns.getNext({
    params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
  });
}

/** @public */
export const getNextProfilesQueryOptions = (workspaceId: number, campaignId: string) =>
  queryOptions({
    queryKey: campaignKeys.nextProfiles(workspaceId, campaignId),
    queryFn: () => getNextProfiles(workspaceId, campaignId),
    // The schedule moves on as people enter, so each look at it asks again.
    staleTime: 0,
  });

interface UseNextProfilesOptionalOptions {
  workspaceId: number;
  campaignId: string;
  enabled?: boolean;
}

/**
 * Non-suspending: the schedule is one tab of a panel that renders without it.
 * `skipToken` while inactive, so a campaign that is not running registers no
 * cache slot.
 *
 * @public
 */
export const useNextProfilesOptional = ({
  workspaceId,
  campaignId,
  enabled = true,
}: UseNextProfilesOptionalOptions) => {
  const active = enabled && workspaceId > 0 && campaignId !== '';
  return useQuery({
    queryKey: active ? campaignKeys.nextProfiles(workspaceId, campaignId) : ['campaign', 'next-profiles', 'inactive'],
    queryFn: active ? () => getNextProfiles(workspaceId, campaignId) : skipToken,
    staleTime: 0,
  });
};

/* ------------------------------------------------------------------------- */
/* getCampaignValidation — content warnings for the review panel              */
/* ------------------------------------------------------------------------- */

/**
 * The campaign's content warnings: Liquid references the trigger cannot fill
 * (`template_warnings`), and in-app or inbox messages whose body renders
 * nothing (`in_app_content_warnings`).
 *
 * @public
 */
export async function getCampaignValidation(
  workspaceId: number,
  campaignId: string,
): Promise<CampaignsValidateResponse> {
  return hydraUiApi.campaigns.getValidate({
    params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
  });
}

/**
 * The states a campaign is checked in: the ones it can be started from. A
 * running or archived campaign is never checked.
 */
const VALIDATED_CAMPAIGN_STATES: ReadonlySet<Campaign['state']> = new Set(['draft', 'stopped']);

/** @public */
export const getCampaignValidationQueryOptions = (workspaceId: number, campaignId: string) =>
  queryOptions({
    queryKey: campaignKeys.validation(workspaceId, campaignId),
    queryFn: () => getCampaignValidation(workspaceId, campaignId),
    // The warnings follow the templates and the trigger, which change outside
    // this cache, so each look at them asks again.
    staleTime: 0,
  });

interface UseCampaignValidationOptions {
  workspaceId: number;
  campaignId: string;
  campaignState: Campaign['state'];
}

/**
 * Non-suspending: the warnings decorate a canvas that renders without them,
 * and a failed check reads as no warnings. Only a draft or stopped campaign is
 * checked; any other state registers no cache slot.
 *
 * @public
 */
export const useCampaignValidation = ({ workspaceId, campaignId, campaignState }: UseCampaignValidationOptions) => {
  const active = workspaceId > 0 && campaignId !== '' && VALIDATED_CAMPAIGN_STATES.has(campaignState);
  return useQuery({
    queryKey: active ? campaignKeys.validation(workspaceId, campaignId) : ['campaign', 'validation', 'inactive'],
    queryFn: active ? () => getCampaignValidation(workspaceId, campaignId) : skipToken,
    staleTime: 0,
  });
};

/**
 * Checks the campaign again. A trigger save calls it, because the trigger
 * decides which Liquid references a message can fill.
 *
 * @public
 */
export function useRefreshCampaignValidation(): (campaignId: string) => void {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useCallback(
    (campaignId: string) => {
      void qc.invalidateQueries({ queryKey: campaignKeys.validation(workspaceId, campaignId) });
    },
    [qc, workspaceId],
  );
}

/* ------------------------------------------------------------------------- */
/* getOpenCampaignDraft — the campaign's open draft, under draft mode         */
/* ------------------------------------------------------------------------- */

/** The error code services answers for a campaign whose draft it cannot read (a v3 campaign). */
const DRAFT_UNSUPPORTED_CODE = 'v3_draft_unsupported';

/**
 * Whether a failed draft read means the campaign has no open draft to show, as Ember's
 * `campaign-draft` service reads it: a 404 is no draft open, and a 403 (the draft-mode gate, or
 * a role that cannot read drafts) or an unsupported campaign cannot carry one.
 */
function holdsNoOpenDraft(error: unknown): boolean {
  const status = getApiClientErrorStatus(error);
  return status === 404 || status === 403 || getApiClientErrorCode(error) === DRAFT_UNSUPPORTED_CODE;
}

/** The campaign's open draft, or `null` when it has none or cannot carry one. */
async function getOpenCampaignDraft(
  workspaceId: number,
  campaignId: string,
): Promise<CampaignsCampaignDraftResponse | null> {
  try {
    return await hydraUiApi.campaigns.getDraft({
      params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
    });
  } catch (error) {
    if (holdsNoOpenDraft(error)) return null;
    throw error;
  }
}

type UseOpenCampaignDraftOptionalOptions = {
  workspaceId: number;
  campaignId: string;
  enabled: boolean;
};

/**
 * Non-suspending: the draft only decorates the header, which renders without it. The draft
 * opens, changes, publishes and closes in the embedded app, outside this cache, so each look at
 * it asks again. `skipToken` while inactive, so a campaign that cannot have a draft registers no
 * cache slot.
 */
export const useOpenCampaignDraftOptional = ({
  workspaceId,
  campaignId,
  enabled,
}: UseOpenCampaignDraftOptionalOptions) => {
  const active = enabled && workspaceId > 0 && campaignId !== '';
  return useQuery({
    queryKey: active ? campaignKeys.openDraft(workspaceId, campaignId) : ['campaign', 'open-draft', 'inactive'],
    queryFn: active ? () => getOpenCampaignDraft(workspaceId, campaignId) : skipToken,
    staleTime: 0,
  });
};

/* ------------------------------------------------------------------------- */
/* getCampaignMetricsSummary — unsent drafts and the queued count             */
/* ------------------------------------------------------------------------- */

/** The two current-state counts the campaign metrics carry beside its series. */
export type CampaignMetricsSummary = {
  /** Whether the campaign drafted a message in the last 30 days that was never sent. */
  unsentDrafts: boolean;
  /** How many of the campaign's deliveries from the last 30 days still wait to be sent. */
  totalQueued: number;
};

/**
 * Reads the campaign's metrics and keeps only the unsent-drafts flag and the
 * queued count. GET campaign always reports `unsent_drafts` as false, so this
 * is where the real value comes from.
 *
 * Both values are counted over the last 30 days whatever window is asked
 * for, so no window is sent. The spec declares no query string for this
 * endpoint, so the backend falls back to its default window.
 *
 * @public
 */
export async function getCampaignMetricsSummary(
  workspaceId: number,
  campaignId: string,
): Promise<CampaignMetricsSummary> {
  const raw = await hydraUiApi.campaigns.getMetrics({
    params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
  });
  return { unsentDrafts: raw.unsent_drafts, totalQueued: raw.metric.total_queued ?? 0 };
}

interface UseCampaignMetricsSummaryOptions {
  workspaceId?: number;
  campaignId: string;
  enabled?: boolean;
}

/**
 * Non-suspending: the values only change what a confirmation says, and the
 * page renders without them. `skipToken` while inactive, so a draft, which
 * has no metrics, registers no cache slot.
 *
 * @public
 */
export const useCampaignMetricsSummary = ({
  workspaceId: overrideWorkspaceId,
  campaignId,
  enabled = true,
}: UseCampaignMetricsSummaryOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const active = enabled && campaignId !== '';
  return useQuery({
    queryKey: active
      ? campaignKeys.metricsSummary(workspaceId, campaignId)
      : ['campaign', 'metrics-summary', 'inactive'],
    queryFn: active ? () => getCampaignMetricsSummary(workspaceId, campaignId) : skipToken,
    // The counts move as messages send, so each look at them asks again.
    staleTime: 0,
  });
};

/* ------------------------------------------------------------------------- */
/* getUntranslatedActions — messages missing a language variant               */
/* ------------------------------------------------------------------------- */

/**
 * The ids of the campaign's messages that sit behind a multi-language branch
 * and lack a variant for one of its languages.
 *
 * @public
 */
export async function getUntranslatedActions(workspaceId: number, campaignId: string): Promise<string[]> {
  return hydraUiApi.campaigns.getUntranslatedActions({
    params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
  });
}

interface UseUntranslatedActionsOptions {
  workspaceId: number;
  campaignId: string;
  /** Without a multi-language branch nothing can be untranslated, so nothing is asked. */
  hasMultiLanguageBranch: boolean;
  /**
   * The number of workflow actions. It is part of the key, so adding or
   * removing an action asks again.
   */
  actionCount: number;
}

/**
 * Non-suspending: the answer only adds rows to the review panel. While a new
 * action count is being asked about, the last answer for the same campaign
 * stays, so the rows do not blink out.
 *
 * @public
 */
export const useUntranslatedActions = ({
  workspaceId,
  campaignId,
  hasMultiLanguageBranch,
  actionCount,
}: UseUntranslatedActionsOptions) => {
  const active = hasMultiLanguageBranch && workspaceId > 0 && campaignId !== '';
  const campaignKey = campaignKeys.untranslatedActions(workspaceId, campaignId);
  return useQuery({
    queryKey: active ? [...campaignKey, actionCount] : ['campaign', 'untranslated-actions', 'inactive'],
    queryFn: active ? () => getUntranslatedActions(workspaceId, campaignId) : skipToken,
    staleTime: 0,
    placeholderData: (previous, previousQuery) =>
      previousQuery != null && partialMatchKey(previousQuery.queryKey, campaignKey) ? previous : undefined,
    meta: { errorMessage: 'The untranslated messages could not be checked.' },
  });
};

/* ------------------------------------------------------------------------- */
/* getProfileMatchTime — when one person would enter a date campaign          */
/* ------------------------------------------------------------------------- */

/**
 * What the match-time preview answers: the times the person would enter, or
 * why they would not.
 *
 * The endpoint refuses a date it cannot schedule with a 422 whose first error
 * says why (`no attribute for customer`, `invalid time attribute`, `invalid
 * time attribute for frequency type`). That refusal is an answer about the
 * person rather than a failed request, so it is returned as `refusal` instead
 * of thrown; anything else still throws.
 */
export type ProfileMatchTime =
  | { kind: 'match'; times: CampaignsProfileMatchTimeResponse }
  | { kind: 'refusal'; detail: string };

const HTTP_UNPROCESSABLE = 422;

/**
 * When the person the request names would enter a date campaign set up as the
 * request describes. It reads rather than writes, though it is a `POST`: the
 * campaign's settings travel in the body, so a trigger can be previewed with
 * values it has not saved.
 *
 * @public
 */
export async function getProfileMatchTime(
  workspaceId: number,
  request: CampaignsProfileMatchTimeRequest,
): Promise<ProfileMatchTime> {
  try {
    const times = await hydraUiApi.campaigns.profileProfileMatchtime({
      params: { path: { environment_id: String(workspaceId) } },
      body: request,
    });
    return { kind: 'match', times };
  } catch (error) {
    if (getApiClientErrorStatus(error) !== HTTP_UNPROCESSABLE) {
      throw error;
    }
    return { kind: 'refusal', detail: getApiClientErrorDetail(getApiClientErrorBody(error)) ?? '' };
  }
}

interface UseProfileMatchTimeOptionalOptions {
  workspaceId: number;
  /** The request to preview, or `null` while there is no person to ask about. */
  request: CampaignsProfileMatchTimeRequest | null;
}

/**
 * Non-suspending: the preview is one part of a panel that renders without it.
 * The request is the key, so choosing another person or changing a setting the
 * body carries asks again. A failure other than a refusal is reported with a
 * snackbar.
 *
 * @public
 */
export const useProfileMatchTimeOptional = ({ workspaceId, request }: UseProfileMatchTimeOptionalOptions) =>
  useQuery({
    queryKey:
      request == null
        ? ['campaign', 'profile-match-time', 'inactive']
        : campaignKeys.profileMatchTime(workspaceId, request),
    queryFn: request == null ? skipToken : () => getProfileMatchTime(workspaceId, request),
    staleTime: STALE_TIME,
    meta: { errorMessage: 'The entry time for this person could not be loaded.' },
  });

/* ------------------------------------------------------------------------- */
/* getObjectDatePreview — who an object date trigger would enter, and when    */
/* ------------------------------------------------------------------------- */

/**
 * One object an object date trigger reads its date off, and the people related
 * to it who would enter, each with when.
 *
 * A POST that only reads. The body is the trigger and audience to evaluate,
 * which do not have to be saved, so an edit in progress can be previewed.
 * Partial settings are answered with an empty result and a `reason` rather
 * than an error.
 *
 * @public
 */
export async function getObjectDatePreview(
  workspaceId: number,
  body: PreviewsObjectDatePreviewRequest,
): Promise<PreviewsObjectDatePreviewResponse> {
  return hydraUiApi.previews.objectDate({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

/**
 * A preview and the request it answers.
 *
 * The request travels with the response because the preview keeps showing
 * while a newer one loads. A caller that words the preview by the settings it
 * was asked with, such as the frequency, reads them from here rather than from
 * the settings being edited, which can already be newer.
 *
 * @public
 */
export type ObjectDatePreview = {
  request: PreviewsObjectDatePreviewRequest;
  response: PreviewsObjectDatePreviewResponse;
};

/** @public */
export const getObjectDatePreviewQueryOptions = (workspaceId: number, body: PreviewsObjectDatePreviewRequest) =>
  queryOptions({
    queryKey: campaignKeys.objectDatePreview(workspaceId, body),
    queryFn: async (): Promise<ObjectDatePreview> => ({
      request: body,
      response: await getObjectDatePreview(workspaceId, body),
    }),
    // Who is related to the object, and their zones, change without the
    // trigger changing, so each look at the preview asks again.
    staleTime: 0,
  });

interface UseObjectDatePreviewOptionalOptions {
  workspaceId: number;
  body: PreviewsObjectDatePreviewRequest;
  enabled: boolean;
}

/**
 * Non-suspending: the preview is one part of a panel that renders without it.
 * `skipToken` while inactive, so a trigger that cannot be previewed yet asks
 * nothing.
 *
 * The last preview stays on screen while the one for an edited trigger loads,
 * rather than the panel blanking to a loading line on every edit.
 *
 * @public
 */
export const useObjectDatePreviewOptional = ({ workspaceId, body, enabled }: UseObjectDatePreviewOptionalOptions) => {
  const options = getObjectDatePreviewQueryOptions(workspaceId, body);
  return useQuery({
    ...options,
    queryFn: enabled ? options.queryFn : skipToken,
    placeholderData: keepPreviousData,
  });
};

/* ------------------------------------------------------------------------- */
/* getCampaignThrottleChannels — the channels a rate limit can cover          */
/* ------------------------------------------------------------------------- */

/**
 * The channel types the workspace can rate limit a campaign on, such as
 * `email` and `twilio`.
 *
 * A failed request answers no channels rather than an error. The rate limit
 * section reads no channels as "show every channel it knows", so a failure
 * never hides a channel the workspace could limit.
 *
 * @public
 */
export async function getCampaignThrottleChannels(workspaceId: number): Promise<string[]> {
  try {
    const raw = await hydraUiApi.campaigns.getCampaignMessageThrottleSupportedChannels({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return raw.channels.map((channel) => channel.name);
  } catch {
    return [];
  }
}

/** @public */
export const getCampaignThrottleChannelsQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: campaignKeys.throttleChannels(workspaceId),
    queryFn: () => getCampaignThrottleChannels(workspaceId),
    staleTime: STALE_TIME,
  });

/* ------------------------------------------------------------------------- */
/* Relation reads — follow FK arrays to entity detail caches                  */
/* ------------------------------------------------------------------------- */

/**
 * Resolve a campaign's `workflow_action_ids` to the cached Action entities.
 *
 * Normalized read: walks `campaign.workflow_action_ids` and follows each
 * id into `actionKeys.detail(wsId, id)`. Each Action lives in exactly one
 * cache slot — its detail. This relation view is derived, not stored.
 *
 * If any referenced action isn't cached, its by-id queryFn fires
 * (suspending). For our cluster sideload pattern, all referenced actions
 * arrived together, so cache hits are the common case.
 */
/** @public */
export function useCampaignActions(campaign: Campaign): readonly Action[] {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const optionsForId = useCallback((id: string) => getActionQueryOptions(workspaceId, id, qc), [workspaceId, qc]);
  return useEntitiesByIds(optionsForId, campaign.workflow_action_ids);
}

/**
 * Belongs-to FK follow: read the campaign an action references via its
 * `campaign_id`. Lives here (not in `action/queries.ts`) to avoid a
 * circular import — the hook returns a `Campaign`, so the campaign
 * module is the honest home.
 *
 * **Non-suspending.** Returns `data: undefined` when the action has no
 * `campaign_id` (orphaned / scratch actions); otherwise resolves from
 * cache (the action's parent list ran first) or fires the by-id
 * queryFn for deep links. Callers must handle `data: undefined` plus
 * the usual `isPending` / `isError` states.
 *
 * Mirrors `useDomainsOptional`'s shape: `useQuery` with `skipToken` for
 * the missing-input case so an absent `campaign_id` doesn't register a
 * sentinel slot in the workspace cache namespace. Throwing during
 * render (the previous shape) made the hook unsafe for components
 * rendered before `campaign_id` settled and risked hooks-rules
 * violations if the field flipped between renders.
 *
 *   const { data: campaign } = useCampaignForAction(action);
 *   if (campaign == null) return null; // no campaign_id, or still loading
 */
/** @public */
export function useCampaignForAction(actionInstance: Action) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const campaignId = actionInstance.campaign_id;
  return useQuery({
    // Sentinel key sits outside the `['workspace', wsId, ...]` namespace
    // so it survives `clearWorkspaceQueries`. `skipToken` guarantees no
    // data ever lands here. Don't `setQueryData` against this key.
    queryKey: campaignId != null ? campaignKeys.detail(workspaceId, campaignId) : ['campaign', 'no-campaign-id'],
    queryFn: campaignId != null ? () => getCampaign(workspaceId, campaignId, qc) : skipToken,
    staleTime: STALE_TIME,
  });
}

/**
 * Resolve a campaign's `tag_ids` to the cached Tag entities. Same FK-follow
 * pattern as `useCampaignActions`.
 */
/** @public */
export function useCampaignTags(campaign: Campaign): readonly Tag[] {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const optionsForId = useCallback((id: string) => getTagQueryOptions(workspaceId, id, qc), [workspaceId, qc]);
  return useEntitiesByIds(optionsForId, campaign.tag_ids);
}

/**
 * Campaigns the cache has seen that reference this tag id.
 *
 * Inverse of campaign↔tag, derived from the cached campaign details
 * rather than a dedicated server view. Lives here (not in
 * `tag/queries.ts`) to break the campaign/tag circular import — it's a
 * campaign read parameterized by tag id.
 *
 * **Not authoritative** — only includes campaigns currently sideloaded
 * (typically from the campaigns list pages the user has visited). With
 * a real "campaigns for tag" backend, replace with a dedicated suspense
 * query.
 */
/**
 * **Re-renders on every cache event under `campaign.details`.** The
 * `select` callback is memoized on `tagId`, so the filter only re-runs
 * when the underlying snapshot changes. Consumers doing further
 * derivation downstream should still wrap their own work in `useMemo`
 * if it's expensive.
 *
 * **Cardinality.** Sized for the same budget as `useAllCached` itself —
 * up to a few hundred cached campaigns. Workspaces routinely caching
 * thousands of campaigns should grow a dedicated paginated
 * `getCampaignsForTag(tagId, page)` query rather than scaling this
 * derived view.
 */
/** @public */
export function useCampaignsForTag(tagId: string): readonly Campaign[] {
  const select = useCallback(
    (campaigns: readonly Campaign[]) => campaigns.filter((c) => c.tag_ids.includes(tagId)),
    [tagId],
  );
  return useCampaignsCached({ select });
}

/* ------------------------------------------------------------------------- */
/* useCampaignsCached — store-wide read of every cached campaign              */
/* ------------------------------------------------------------------------- */

/**
 * Reactive read of every campaign currently in the cache, across all
 * detail slots, regardless of which list page or detail fetch loaded
 * them. **No fetch fires** — purely a cache subscription.
 *
 * Use to derive views without coupling to a specific list query:
 *
 *   // All campaigns currently cached:
 *   const all = useCampaignsCached();
 *
 *   // Campaigns referencing a tag (the `useCampaignsForTag` use case
 *   // we previously had as a dedicated inverse cache slot):
 *   const tagged = useCampaignsCached({
 *     select: (cs) => cs.filter((c) => c.tag_ids.includes(tagId)),
 *   });
 *
 * Re-renders when any campaign detail slot is added, updated, or removed.
 * Returns a stable array reference when nothing has changed (so render
 * skipping works in consumers). `select` is a derivation hook — wrap in
 * `useMemo` if your filter creates non-stable references and you care.
 *
 * Compared to a list query: this is bound to the entity table, not to
 * any one query. Whatever's been sideloaded — by list, by detail, by
 * mutation response — shows up here. Pages getting GC'd don't lose data
 * from the entity table; this hook follows.
 */
/** @public */
/** Every cached campaign in the workspace. */
export function useCampaignsCachedForWorkspace(workspaceId: number): readonly Campaign[];
/** Every cached campaign in the workspace, mapped through `select`. */
export function useCampaignsCachedForWorkspace<TSelected>(
  workspaceId: number,
  options: Required<UseAllCachedOptions<Campaign, TSelected>>,
): TSelected;
export function useCampaignsCachedForWorkspace<TSelected>(
  workspaceId: number,
  options?: UseAllCachedOptions<Campaign, TSelected>,
): TSelected | readonly Campaign[];
export function useCampaignsCachedForWorkspace<TSelected>(
  workspaceId: number,
  options?: UseAllCachedOptions<Campaign, TSelected>,
): TSelected | readonly Campaign[] {
  return useAllCached<Campaign, TSelected>(campaignKeys.details(workspaceId), options);
}

/** @public */
/** Every cached campaign in the workspace. */
export function useCampaignsCached(): readonly Campaign[];
/** Every cached campaign in the workspace, mapped through `select`. */
export function useCampaignsCached<TSelected>(options: Required<UseAllCachedOptions<Campaign, TSelected>>): TSelected;
export function useCampaignsCached<TSelected>(
  options?: UseAllCachedOptions<Campaign, TSelected>,
): TSelected | readonly Campaign[];
export function useCampaignsCached<TSelected>(
  options?: UseAllCachedOptions<Campaign, TSelected>,
): TSelected | readonly Campaign[] {
  const workspaceId = requireWorkspaceId();
  return useCampaignsCachedForWorkspace(workspaceId, options);
}

/* ------------------------------------------------------------------------- */
/* getApiTriggeredBroadcasts — the Broadcasts API-triggered tab               */
/* ------------------------------------------------------------------------- */

/**
 * An API-triggered broadcast *is* a campaign of this type — the Ember route
 * loads one with `store.findRecord('campaign', …)` and redirects anything
 * that isn't this type over to the campaigns section.
 */
export const API_TRIGGERED_CAMPAIGN_TYPE = 'triggered_broadcast';

/**
 * One page of the Broadcasts API-triggered tab.
 *
 * Deliberately *not* `getCampaigns`. That read pins `version: '2'` and
 * either `excludeMetrics: true` or a metrics window; this one sends neither,
 * matching the Ember query, because the tab reads `sent` and `delivered` off
 * each row for its "in the last 24 hours" columns and those params select
 * different metrics paths in the handler.
 *
 * Shares the cluster sideload with the other campaign reads, so a row's
 * tags and subscription topic resolve from cache.
 *
 * @public
 */
export async function getApiTriggeredBroadcasts(
  workspaceId: number,
  params: ApiTriggeredBroadcastListParams,
  qc: QueryClient,
): Promise<ParsedCampaignsListResponse> {
  const raw = await hydraUiApi.campaigns.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: {
        type: API_TRIGGERED_CAMPAIGN_TYPE,
        page: params.page - 1, // 1-based at the hook boundary, 0-based on the wire
        state: params.state,
        search: params.search,
        sort: params.sort,
        asc: params.asc,
        sendToUnsubscribed: params.sendToUnsubscribed,
        subscriptionTopicId: params.subscriptionTopicId,
        'tagIds[]': toWireIds(params.tagIds),
        'objectTypeIds[]': toWireIds(params.objectTypeIds),
        'goalIds[]': toWireIds(params.goalIds),
      },
    },
  });
  const response = parseCampaignsListResponse(raw);
  populateCampaignsList(qc, workspaceId, response);
  return response;
}

/**
 * Never cached, for the same reason the newsletters tab isn't: Ember reads
 * this through `store.query`, and a searched, filtered, sorted, paged list
 * must not serve a stale page.
 *
 * @public
 */
export const getApiTriggeredBroadcastsQueryOptions = (
  workspaceId: number,
  params: ApiTriggeredBroadcastListParams,
  qc: QueryClient,
) =>
  queryOptions({
    queryKey: campaignKeys.apiTriggeredList(workspaceId, params),
    queryFn: () => getApiTriggeredBroadcasts(workspaceId, params, qc),
    staleTime: 0,
  });

interface UseApiTriggeredBroadcastsOptions {
  workspaceId?: number;
  params: ApiTriggeredBroadcastListParams;
  /**
   * Builder accounts get a 403 from the campaigns endpoint, and the tab is
   * hidden for them, so the caller disables the read rather than letting it
   * fail. Ember skips the fetch for the same reason.
   */
  enabled?: boolean;
  queryConfig?: QueryConfig<typeof getApiTriggeredBroadcastsQueryOptions>;
}

/**
 * The Broadcasts API-triggered tab's list read.
 *
 * Non-suspense for the same reasons as the newsletters tab: the Ember page
 * degrades a failed fetch to an empty tab instead of the app-wide error
 * substate, and it distinguishes "not loaded" from "loaded and empty" when
 * deciding whether to show the welcome state.
 *
 * @public
 */
export const useApiTriggeredBroadcasts = ({
  workspaceId: overrideWorkspaceId,
  params,
  enabled = true,
  queryConfig,
}: UseApiTriggeredBroadcastsOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const options = getApiTriggeredBroadcastsQueryOptions(workspaceId, params, qc);
  return useQuery({
    ...options,
    queryFn: enabled ? options.queryFn : skipToken,
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* useCampaignRecentMetric                                                   */
/* ------------------------------------------------------------------------- */

interface UseCampaignRecentMetricOptions {
  workspaceId: number;
  campaignId: string;
  /** The workspace's metrics timezone, which the daily bins are cut in. */
  timeZone: string;
  enabled?: boolean;
}

/**
 * A campaign's email delivery metrics over the latest daily bins, as the Broadcasts API-triggered
 * list reads them for its last-30-days columns (Ember's `fetchMetric` with `version: 2`).
 * Non-suspense, so a row fills in on its own, and fresh for 30 seconds, as Ember's metrics service
 * reuses a read that recent.
 */
export const useCampaignRecentMetric = ({
  workspaceId,
  campaignId,
  timeZone,
  enabled = true,
}: UseCampaignRecentMetricOptions) =>
  useQuery({
    queryKey: campaignKeys.recentMetric(workspaceId, campaignId, timeZone),
    queryFn: enabled
      ? async (): Promise<Metric> => {
          const raw = await hydraUiApi.campaigns.getMetrics({
            params: {
              path: { environment_id: String(workspaceId), campaign_id: campaignId },
              query: { version: '2', res: 'days', tz: timeZone },
            },
          });
          // The metric carries its own `unsent_drafts`, which Ember's row reads. The handler sets it
          // and the top-level one `getCampaignMetricsSummary` reads from the same count
          // (services `ui_api/campaigns/metrics.go`), so the two readers agree.
          return raw.metric;
        }
      : skipToken,
    staleTime: 30_000,
  });

/* ------------------------------------------------------------------------- */
/* getBroadcastTriggers — an API-triggered broadcast's previous sends         */
/* ------------------------------------------------------------------------- */

/** An API-triggered broadcast's previous sends, newest first, with the data each was triggered with. */
export async function getBroadcastTriggers(workspaceId: number, campaignId: string): Promise<BroadcastTrigger[]> {
  const reply = await hydraUiApi.broadcasts.getCampaignsTriggers({
    params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
  });
  return 'triggers' in reply ? reply.triggers : [];
}

/** Read fresh each time, since a broadcast can be triggered again at any moment. */
export const getBroadcastTriggersQueryOptions = (workspaceId: number, campaignId: string) =>
  queryOptions({
    queryKey: campaignKeys.broadcastTriggers(workspaceId, campaignId),
    queryFn: () => getBroadcastTriggers(workspaceId, campaignId),
    staleTime: 0,
  });
